import { FC, useEffect, useState } from "react";
import { PolicyQuoteInsuredAndActiveSetterProps } from "../PolicyQuoteForm/PolicyQuoteTypes";
import PolicyQuoteBindInstructionsForm from "./PolicyQuoteBindInstructionsForm";
import PolicyQuoteBindInstructionsTables from "./PolicyQuoteBindInstructionsTables";
import { useAtomFamily } from "../../../../hooks/useAtomFamily";
import { INSURED_ATOM_KEY } from "../../../../utilities/queryStringsHash";
import { GlobalInsuredAtomFamily } from "../../InsuredAtoms";
import { getTotalSurchargesAndFees } from "../PolicyQuoteExposurePremium/ExposurePremiumUtils";
import { getTotalPremium } from "../PolicyQuoteForm/PolicyQuoteUtils";
import {
  getDepositTypeByOverride,
  calculatedBilledDepositAmount,
  getStatesWithRatings,
  depositTypes,
} from "./PolicyQuoteBindInstructionsUtils";
import { useSetRecoilState } from "recoil";
import { BindInstructionsInfoAtom } from "./PolicyQuoteBindInstructionsAtoms";
import SaveEndorsementFormsInformation from "./SaveEndorsementFormsInformation";
import { QuotePolicySectionEnum } from "../../../../dtos/quote-policy-section-enum";
import { validatePreviousSections } from "../PolicyQuoteForm/PolicyQuoteValidationUtils";
import { updatePolicyQuoteInformation } from "../updatesPolicyQuoteFunctions";
import ReviewPolicyValidations from "./ReviewPolicyValidations";
import PolicyQuoteBottomButtons from "../PolicyQuoteForm/PolicyQuoteBottomButtons";
import PolicyQuoteBindInstructionsBindPolicy from "./PolicyQuoteBindInstructionsBindPolicy";
import styles from "./PolicyQuoteBindInstructionsStyles.module.css";
import PolicyQuoteBindInstructionsSaveAtom from "./PolicyQuoteBindInstructionsSaveAtom";

const PolicyQuoteBindInstructions: FC<
  PolicyQuoteInsuredAndActiveSetterProps
> = ({ tabKey, readOnly, setActiveSection, insuredId, quoteStatus }) => {
  const setBindInstructionsInfo = useSetRecoilState(BindInstructionsInfoAtom);
  const [readyToValidateAlerts, setReadyToValidateAlerts] =
    useState<boolean>(false);
  const insuredIdAtomKey = `${INSURED_ATOM_KEY} ${tabKey}`;
  const { getAtom, setAtom } = useAtomFamily(
    GlobalInsuredAtomFamily(insuredIdAtomKey)
  );

  const policyJSON = getAtom()?.policyQuoteInformation?.policyQuote;

  const calculateAndFillBindInstructionsValues = () => {
    const statesWithRatings = getStatesWithRatings(
      policyJSON?.quote?.states ?? []
    );
    const payPlan = policyJSON?.payPlan;
    const depositOverride = payPlan?.depositOverride === 1;

    const totalPremium = getTotalPremium(statesWithRatings);
    const totalFees = getTotalSurchargesAndFees(statesWithRatings);
    const prorateToTerm = false;
    const billedPremium = policyJSON?.totalPremium ?? 0;
    const billedFees = policyJSON?.totalFees ?? 0;
    const billingMethod = policyJSON?.bindInstructions?.billingMethod ?? null;
    const depositType = getDepositTypeByOverride(
      depositOverride,
      policyJSON?.bindInstructions?.depositTypeForDepositOverride,
      payPlan?.depositType
    );

    const depositRate = policyJSON?.payPlan?.depositRate ?? null;
    const { billedDepositAmount, installmentPayment, nonEarningAmount } =
      calculatedBilledDepositAmount(
        payPlan,
        policyJSON?.totalPremium ?? 0,
        depositType
      );
    const billByLocation =
      policyJSON?.bindInstructions?.billByLocation ?? false;
    const policyType = policyJSON?.policyType?.value;
    const states = policyJSON?.quote?.states ?? [];
    const firstPolicyDate = policyJSON?.firstPolicyDate;
    const effectiveDate = policyJSON?.effectiveDate;
    const expirationDate = policyJSON?.expirationDate;
    const addresses = policyJSON?.insured?.addresses;
    const invoices = policyJSON?.invoices;
    const bindDiscountAmount = policyJSON?.bindInstructions?.bindDiscountAmount;

    setBindInstructionsInfo({
      readOnly,
      payPlan,
      totalPremium,
      totalFees,
      prorateToTerm,
      billedPremium,
      billedFees,
      billingMethod,
      depositType,
      depositRate,
      billedDepositAmount,
      installmentPayment,
      nonEarningAmount,
      bindDiscountAmount,
      billByLocation,
      effectiveDate,
      firstPolicyDate,
      expirationDate,
      policyType,
      states,
      addresses,
      invoices,
      dispatchTable: billingMethod !== depositTypes.REPORTING,
    });
  };

  const returnToPolicyQuoteInformationSection = () => {
    const atomValue = getAtom();
    const newAtomValue = updatePolicyQuoteInformation(
      atomValue,
      "activeSection",
      QuotePolicySectionEnum.INFORMATION
    );
    setAtom(newAtomValue);
    setActiveSection?.(QuotePolicySectionEnum.INFORMATION);
  };

  const validateInformationToRunBindInstructions = () => {
    const atomValue = getAtom();
    const activeSection =
      atomValue?.policyQuoteInformation?.activeSection ??
      QuotePolicySectionEnum.INFORMATION;
    const isValid = validatePreviousSections(atomValue, activeSection);
    setReadyToValidateAlerts(isValid);
    if (isValid) {
      calculateAndFillBindInstructionsValues();
    } else {
      returnToPolicyQuoteInformationSection();
    }
  };

  useEffect(() => {
    validateInformationToRunBindInstructions();
  }, []);

  return (
    <div className={styles.policy_quote_bind_instructions_general_container}>
      <div
        className={styles.policy_quote_bind_instructions_components_container}
      >
        <div className={styles.policy_quote_bind_instructions_container}>
          <div
            className={styles.policy_quote_bind_instructions_internal_container}
          >
            <div className={"policy_quote_bind_instructions_tables_container"}>
              <PolicyQuoteBindInstructionsForm
                tabKey={tabKey}
                readOnly={readOnly}
              />
              <PolicyQuoteBindInstructionsTables
                tabKey={tabKey}
                readOnly={readOnly}
              />
            </div>
          </div>
          <PolicyQuoteBindInstructionsBindPolicy
            insuredId={insuredId}
            tabKey={tabKey}
            readOnly={readOnly}
          />
        </div>
      </div>
      <PolicyQuoteBottomButtons
        tabKey={tabKey}
        insuredId={insuredId}
        policySection={QuotePolicySectionEnum.BIND_INSTRUCTIONS}
        quoteStatus={quoteStatus}
        setActiveSection={setActiveSection}
      />
      <SaveEndorsementFormsInformation tabKey={tabKey} />
      <PolicyQuoteBindInstructionsSaveAtom tabKey={tabKey} />
      {readyToValidateAlerts === true && (
        <ReviewPolicyValidations tabKey={tabKey} />
      )}
    </div>
  );
};

export default PolicyQuoteBindInstructions;
